import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { departmentLabel } from "@/lib/department-label";

interface CoverageAreaPickerProps {
  /** The vocabulary, from the server. Never a client-side constant. */
  available: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  idPrefix?: string;
  /**
   * The chip row's own classes, when a surface wants a different density.
   * Chips wrap on their own, so this is rarely needed — it exists for a
   * caller that has to tighten or loosen the spacing.
   */
  wrapClassName?: string;
}

/**
 * The departments a professional covers, as a checkbox grid (spec 8ec68023 D1).
 *
 * EXTRACTED SO ACCEPTANCE CAN ASK FOR IT. The grid lived only inside
 * CoverageAreasCard, which sits on a profile screen reachable after signup —
 * and 0 of 8 professionals had ever found it, so the ranking built on this data
 * had never once changed an ordering (task 4228162). Coverage feeds matching,
 * and an attribute that feeds matching has to be asked for while the
 * professional is actually present.
 *
 * STATELESS ON PURPOSE. The card owns a save button and a mutation; the accept
 * pages submit it with everything else at the end of onboarding. Those are
 * different lifecycles over the same question, so the shared piece is the
 * question, not the saving.
 *
 * NOT REQUIRED, ANYWHERE. Covering nothing is a legitimate answer and shows
 * every request unranked (control C-4). Blocking on it would push professionals
 * into ticking departments they do not serve, which is worse than an empty set
 * because it looks like data.
 */
export function CoverageAreaPicker({
  available,
  selected,
  onChange,
  disabled,
  idPrefix = "coverage",
  wrapClassName = "flex flex-wrap gap-2",
}: CoverageAreaPickerProps): JSX.Element {
  const { t } = useTranslation();

  const toggle = (slug: string, on: boolean): void => {
    onChange(on ? [...selected, slug] : selected.filter((d) => d !== slug));
  };

  /*
    WHOLE-COUNTRY COVERAGE, DECLARED BY THE PROFESSIONAL — which is the entire
    reason this control exists rather than a backfill.

    Setting every department for the existing population was considered and
    rejected: coverage never affects who RECEIVES work (the broadcast service
    does not read it), so a backfill buys no reach. What it does drive is the
    "en tu zona" mark, and marking every request for everybody makes the mark
    meaningless — it is precisely what lets a professional in Rivera notice a
    Rivera job that was NOT marked, which is the only way anyone could ever
    catch the ranking being wrong. An empty set is honestly neutral; all
    nineteen asserted on someone's behalf is just false.

    So the same end state stays one click away for anyone who genuinely covers
    the country, and it is THEIR claim.
  */
  const allSelected =
    available.length > 0 && available.every((slug) => selected.includes(slug));

  const toggleAll = (on: boolean): void => {
    onChange(on ? [...available] : []);
  };

  return (
    <div className="space-y-4">
      {/*
        WHOLE-COUNTRY SITS APART FROM THE LIST, in its own bordered row.
        It is not a twentieth department — it is a claim ABOUT the list — and
        as one more checkbox directly above nineteen identical ones it read as
        the first item of the grid. Braintrust separates its "United States
        only" the same way, for the same reason.

        It stays a Checkbox rather than becoming a chip: its state is
        DERIVED (every department selected), so a toggle that can be half-true
        needs the control that can show it.
      */}
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/40 px-3.5 py-3 dark:bg-muted/20">
        <Checkbox
          id={`${idPrefix}-all-country`}
          checked={allSelected}
          onCheckedChange={(v) => toggleAll(v === true)}
          disabled={disabled}
        />
        <Label
          htmlFor={`${idPrefix}-all-country`}
          className="cursor-pointer font-medium"
        >
          {t("common.coverageAreaPicker.allCountry")}
        </Label>
      </div>

      {/*
        THE DEPARTMENTS ARE CHIPS, not a checkbox grid. Nineteen checkboxes in
        three columns is a form to be audited row by row; the same nineteen as
        toggles is a set to be picked from, which is the actual task — and it
        is what every reference does with a known vocabulary of this size
        (Apollo, Contra, Vanta). The chips also give a target the whole label
        wide instead of a 16px box.
      */}
      <div className={wrapClassName}>
        {available.map((slug) => {
          const on = selected.includes(slug);
          return (
            <button
              key={slug}
              id={`${idPrefix}-${slug}`}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => toggle(slug, !on)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                on
                  ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border-border bg-background text-foreground hover:border-foreground/30 hover:bg-muted",
                disabled && "cursor-not-allowed opacity-60"
              )}
            >
              {departmentLabel(slug)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
